// The longest period one cost can cover. A longer one is a typing error
// (2000 to 2050) rather than an invoice, and it would spread the cost over
// decades of tenants. Mirrored in rentio-api-next (MAX_COST_PERIOD_YEARS).
export const MAX_COST_PERIOD_YEARS = 5;

const YEAR_DIGITS = 4;
const ISO_DAY_LENGTH = 10;

// The same calendar day `years` later, as "YYYY-MM-DD". A 29 February stays
// the 29th, which still sorts between 28 February and 1 March.
const addYearsToIsoDay = (params: { day: string; years: number }): string => {
  const { day, years } = params;
  const year = Number(day.slice(0, YEAR_DIGITS)) + years;

  return `${String(year).padStart(YEAR_DIGITS, "0")}${day.slice(YEAR_DIGITS)}`;
};

// Whether a period covers more than MAX_COST_PERIOD_YEARS: from 1 January 2026
// it can run up to 31 December 2030 at most.
export const isCostPeriodTooLong = (period: {
  from: string;
  to: string;
}): boolean =>
  period.to.slice(0, ISO_DAY_LENGTH) >=
  addYearsToIsoDay({
    day: period.from.slice(0, ISO_DAY_LENGTH),
    years: MAX_COST_PERIOD_YEARS,
  });

// ----- A period as the cost and settlement lists write it -----

interface IPeriodDay {
  year: number;
  month: number;
  day: number;
}

const parseIsoDay = (iso?: string): IPeriodDay | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  if (!match) return undefined;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const fullDay = ({ year, month, day }: IPeriodDay) =>
  `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;

// A cost's day as the cost list writes it, "15/01/2027" (its invoice date);
// a dash without one.
export const costDayText = (iso?: string): string => {
  const day = parseIsoDay(iso);
  return day ? fullDay(day) : "—";
};

// A period as both lists write it, "01/01/2026 → 30/06/2026": the settlement
// list and the cost list above it use this one function, so the two can never
// write a period differently. On a narrow screen it breaks after the arrow,
// the second day going to its own line (see periodRangeLines).
const PERIOD_ARROW = " → ";

export const periodRangeText = (params: { from?: string; to?: string }) =>
  `${costDayText(params.from)}${PERIOD_ARROW}${costDayText(params.to)}`;

// The two pieces a period's text may break between, "01/01/2026 →" and
// "30/06/2026", each kept on one line by the lists, so a narrow column breaks
// it after the arrow only, never before it or inside a day, as the summary
// does. A text without the arrow (a single day) is one piece.
export const periodRangeLines = (text: string): string[] => {
  const at = text.indexOf(PERIOD_ARROW);
  if (at < 0) return [text];
  return [
    `${text.slice(0, at)}${PERIOD_ARROW.trimEnd()}`,
    text.slice(at + PERIOD_ARROW.length),
  ];
};

// ----- The period's quick picks -----

export type TPeriodPresetId =
  | "last-month"
  | "this-quarter"
  | "last-quarter"
  | "this-year"
  | "last-year";

export interface IPeriodPreset {
  id: TPeriodPresetId;
  from: string;
  to: string;
}

const MONTHS_PER_QUARTER = 3;
const MONTHS_PER_YEAR = 12;

// A whole calendar month, from its first to its last day, as "YYYY-MM-DD".
// The month counts from 0 and may run past either end of the year: -1 is
// December of the year before. Day 0 of the next month is the month's last
// day, so a February has 28 or 29 days as its year has.
const monthPeriod = (year: number, month: number) => {
  const first = new Date(Date.UTC(year, month, 1));
  const last = new Date(Date.UTC(year, month + 1, 0));
  const isoDay = (date: Date) => date.toISOString().slice(0, ISO_DAY_LENGTH);
  return { from: isoDay(first), to: isoDay(last) };
};

// The period's quick picks, built off `now` in the user's own time zone (in
// UTC, 1 January before 01:00 in Belgium is still last year), from short to
// long: this and last quarter, this and last year. The cost form also offers
// the whole previous calendar month, first (Lewis, 2026-10-19).
export const periodPresets = (params: {
  now: Date;
  withLastMonth?: boolean;
}): IPeriodPreset[] => {
  const { now, withLastMonth = false } = params;
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarterStart = month - (month % MONTHS_PER_QUARTER);
  const span = (id: TPeriodPresetId, first: number, months: number) => ({
    id,
    from: monthPeriod(year, first).from,
    to: monthPeriod(year, first + months - 1).to,
  });
  const thisYear = span("this-year", 0, MONTHS_PER_YEAR);
  const lastYear = span("last-year", -MONTHS_PER_YEAR, MONTHS_PER_YEAR);
  const thisQuarter = span("this-quarter", quarterStart, MONTHS_PER_QUARTER);
  const lastQuarter = span(
    "last-quarter",
    quarterStart - MONTHS_PER_QUARTER,
    MONTHS_PER_QUARTER,
  );
  const shortToLong = [thisQuarter, lastQuarter, thisYear, lastYear];
  return withLastMonth
    ? [span("last-month", month - 1, 1), ...shortToLong]
    : shortToLong;
};
